import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { API_CONFIG } from '@config/index.ts';

export class ApiClient {
    private static instance: ApiClient;
    private axiosInstance: AxiosInstance;

    private constructor() {
        this.axiosInstance = axios.create({
            baseURL: API_CONFIG.BASE_URL,
            timeout: API_CONFIG.TIMEOUT,
            headers: {
                'Content-Type': 'application/json',
            },
        });

        this.setupInterceptors();
    }

    static getInstance(): ApiClient {
        if (!ApiClient.instance) {
            ApiClient.instance = new ApiClient();
        }
        return ApiClient.instance;
    }

    private setupInterceptors(): void {
        // Request interceptor
        this.axiosInstance.interceptors.request.use(
            (config) => {
                const token = localStorage.getItem('auth_token');
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                return config;
            },
            (error) => Promise.reject(error)
        );

        // Response interceptor
        this.axiosInstance.interceptors.response.use(
            (response) => response.data,
            (error) => {
                if (error.response?.status === 401) {
                    // Handle unauthorized
                    window.location.href = '/login';
                }
                return Promise.reject(error);
            }
        );
    }

    async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
        return this.axiosInstance.get(url, config);
    }

    async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
        return this.axiosInstance.post(url, data, config);
    }

    async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
        return this.axiosInstance.put(url, data, config);
    }

    async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
        return this.axiosInstance.delete(url, config);
    }

    async stream(
        url: string,
        data: any,
        onChunk: (chunk: string) => void,
        onMetadata?: (metadata: any) => void,
        onToolStatus?: (tool: string, status: string) => void
    ): Promise<void> {
        const token = localStorage.getItem('auth_token');

        const response = await fetch(`${API_CONFIG.BASE_URL}/api${url}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': token ? `Bearer ${token}` : '',
                // Remove hardcoded API key - use auth token instead
            },
            body: JSON.stringify(data),
        });

        if (!response.ok) {
            const errorText = await response.text();
            console.error('Stream request failed:', response.status, errorText);
            throw new Error(`Stream request failed: ${response.status}`);
        }

        const reader = response.body?.getReader();
        const decoder = new TextDecoder();

        if (!reader) {
            throw new Error('No reader available');
        }

        let buffer = '';

        while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            // Decode the chunk and add to buffer
            buffer += decoder.decode(value, { stream: true });

            // Process complete SSE messages from buffer
            const messages = buffer.split('\n\n');

            // Keep the last (potentially incomplete) message in the buffer
            buffer = messages.pop() || '';

            for (const message of messages) {
                if (!message.trim()) continue;

                // Parse SSE format
                const lines = message.split('\n');
                let eventData = '';

                for (const line of lines) {
                    if (line.startsWith('data:')) {
                        eventData = line.slice(5).trim();
                    }
                }

                // Handle the data
                if (eventData) {
                    if (eventData === '[DONE]') {
                        return;
                    }

                    try {
                        const parsed = JSON.parse(eventData);

                        // Handle all the same event types as admin
                        if (parsed.type === 'metadata') {
                            if (onMetadata) {
                                onMetadata(parsed);
                            }
                        } else if (parsed.type === 'tool_status') {
                            if (onToolStatus) {
                                onToolStatus(parsed.tool, parsed.status);
                            }
                        } else if (
                            // Accept various content types
                            parsed.type === 'assistant' ||
                            parsed.type === 'content' ||
                            parsed.type === 'conversation' ||
                            parsed.type === 'recommendations' ||
                            parsed.type === 'search_results' ||
                            parsed.type === 'message' ||
                            parsed.type === 'chunk'
                        ) {
                            if (parsed.content) {
                                onChunk(parsed.content);
                            }
                        } else if (parsed.type === 'separator') {
                            const separatorContent = parsed.content || '\n\n';
                            onChunk(separatorContent);
                        } else if (parsed.type === 'done') {
                            return;
                        } else if (parsed.type === 'error') {
                            console.error('Stream error:', parsed.message);
                            throw new Error(parsed.message || 'Stream error');
                        }
                    } catch {
                        // Ignore JSON parse errors for incomplete data
                    }
                }
            }
        }

        // Process any remaining data in buffer
        if (buffer.trim()) {
            const lines = buffer.split('\n');
            for (const line of lines) {
                if (line.startsWith('data:')) {
                    const eventData = line.slice(5).trim();
                    try {
                        const parsed = JSON.parse(eventData);
                        if (parsed.content) {
                            onChunk(parsed.content);
                        }
                    } catch {
                        // Ignore parse errors
                    }
                }
            }
        }
    }
}